/**
 * ProductWithHelpers -> ProductWithHelpers = CURRENT | LOAN | SAVINGS
 * externalProduct -> ExternalProduct = INSURANCE | SECURITIES | LEASING | BUILDINGSOCIETY | PENSION
 * product -> ProductWithHelpers | ExternalProduct | CardAccount
 * */
import { useCallback, useEffect, useMemo } from 'react';

import { normalize } from '@luna/utils';
import type { Account } from '@shared/products-types';
import { logger } from '@luna/logger';
import { useApiSliceSelector } from '@luna/api';

import type { AccountStrategy } from '../../configurations/accountsStrategySingleton';
import { getAccountStrategyOrThrowError } from '../../configurations/accountsStrategySingleton';
import type {
    AccountOrAccountOfExternalProduct,
    ProductWithHelpers,
} from '../../types/productHelpers';
import { mergeGAPIAndWebAPIAccounts } from '../../services/mergeService';
import {
    enrichProducts,
    queryRegisteredProductsIfNeeded,
    retrieveMergedSelectors,
    retrieveMergedSliceIds,
} from '../../services/productsFacadeService';
import { useAccountImages } from '../../accountImagesSlice';

// all service functions by product type should use the derivedType field to narrow down the type.
// type ProductWithHelpers = { id: string; derivedType: 'CURRENT' | 'SAVINGS' | 'LOAN' };
// type ExternalProduct = { id: string; derivedType: 'INSURANCE' | 'SECURITIES' };
// type CardAccount = { id: string; derivedType: 'CURRENT' | 'SAVINGS' | 'LOAN' };
// type Product = ProductWithHelpers | ExternalProduct | CardAccount;

type UseProductsReturn<BaseType, UnderlyingData> = {
    products: ProductWithHelpers<BaseType, UnderlyingData>[];
    productsById: Record<string, ProductWithHelpers<BaseType, UnderlyingData>>;
    refetch: () => Promise<void>;
    isPending: boolean;
    error?: Error | null;
};

/** @deprecated do not introduce more usages of this hook in @retail or @business packages! */
export const useLegacyProducts = <
    BaseType extends AccountOrAccountOfExternalProduct = Account,
    UnderlyingData = unknown,
>(
    strategy?: AccountStrategy,
): UseProductsReturn<BaseType, UnderlyingData> => {
    const { useGapiAccounts, useWebAPIAccounts } = useMemo(() => {
        if (strategy) {
            return strategy;
        }
        return getAccountStrategyOrThrowError();
    }, [strategy]);
    const {
        refetch: refetchGapi,
        isPending: isGapiPending,
        allEntities: allGapiAccounts,
        lastUpdated: gapiAccountLastUpdated,
        error: gapiError,
    } = useGapiAccounts();

    const {
        refetch: refetchWebAPI,
        isPending: isWebAPIPending,
        entities: webAPIAccounts,
        lastUpdated: webAPIAccountLastUpdated,
        error: webAPIError,
    } = useWebAPIAccounts?.() || {};

    useEffect(() => {
        // we need to fetch gapi/webapi first before querying other products
        // this is currently done (in retail) before the load of the UI
        if (webAPIAccountLastUpdated && gapiAccountLastUpdated) {
            queryRegisteredProductsIfNeeded().catch((e) => {
                logger.error('Querying products failed', e);
            });
        }
    }, [webAPIAccountLastUpdated, gapiAccountLastUpdated]);

    const refetch = useCallback(async (): Promise<void> => {
        await Promise.all([refetchGapi(), refetchWebAPI?.()]).catch((e) => {
            throw e;
        });
    }, []);

    /**
     * All strategies (and selectors) must be loaded before 1st render, or it will be ignored
     */
    const slideIds = useMemo(() => retrieveMergedSliceIds(), []);
    const selectors = useMemo(() => retrieveMergedSelectors(), []);
    const dataUsedForEnrichment = useApiSliceSelector(selectors, slideIds);
    const { images } = useAccountImages();

    const [products, productsById] = useMemo(() => {
        const newProducts = enrichProducts<BaseType, UnderlyingData>(
            mergeGAPIAndWebAPIAccounts(allGapiAccounts, webAPIAccounts || {}),
        );
        const newProductsById =
            normalize<ProductWithHelpers<BaseType, UnderlyingData>>(newProducts).byId;
        return [newProducts, newProductsById];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allGapiAccounts, webAPIAccounts, images, dataUsedForEnrichment]);

    const isPending = useMemo(
        () => isGapiPending || isWebAPIPending || false,
        [isGapiPending, isWebAPIPending],
    );
    const error = useMemo(() => gapiError || webAPIError, [gapiError, webAPIError]);

    return { products, productsById, refetch, isPending, error };
};
